/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import useGameState from '../../hooks/useGameState';
import useWindowSize from '../../hooks/useWindowSize';
import { getComponentFromName, getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import TinderArrowIcon from '../icons/TinderArrowIcon';
import * as IconComponents from '../icons/index';
import QuestionsIndicator from '../lists/QuestionsIndicator';
import QuestionsResults from '../lists/QuestionsResults';
import Body from '../partials/Body';
import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/tinder.module.scss';

import hand from '../../../img/hand.svg';

interface TinderGameProps {
    game: TinderGame;
    participation: Participation;
    className?: string;
    reload: () => void;
    canPlay: boolean;
}

function TinderGame({
    className = null,
    game,
    participation,
    canPlay = false,
    reload,
}: TinderGameProps) {
    const themeClassName = useThemeClassName(styles);

    const { width } = useWindowSize();
    const route = useUrlGenerator();
    const {
        choices = [],
        questions = [],
        title = null,
        subtitle = null,
        description = null,
        headerImage = null,
        theme: gameTheme = null,
    } = game || {};

    const {
        questionsIndicatorWithProgress = true,
        questionsIndicatorIconComponent = null,
        questionsIndicatorCurrentIcon = null,
        withQuestionsResults = false,
        questionsResultsItemComponent = null,
        questionsResultsWithBody = true,
    } = gameTheme || {};

    const { currentQuestionIndex, currentQuestion, allAnswersValid } = useGameState(
        game,
        participation,
    );

    const mediumImage = getImageAtSize(headerImage, 'medium');
    const { url: mediumImageUrl = null } = mediumImage || {};

    const { ended, answers: userAnswers = [] } = participation || {};
    const tracking = useTracking();

    const { choices: questionChoices = null } = currentQuestion || {};
    const finalChoices = questionChoices !== null ? questionChoices : choices;

    const [
        {
            growingScale,
            imageScale,
            ctaScale,
            imageX,
            imageRotate,
            leftAnswerOpacity,
            rightAnswerOpacity,
            allAnswersOpacity,
        },
        api,
    ] = useSpring(() => ({
        growingScale: 0,
        ctaScale: 1,
        imageScale: 1,
        imageX: 0,
        imageRotate: 0,
        leftAnswerOpacity: 0,
        rightAnswerOpacity: 0,
        allAnswersOpacity: 0,
    }));

    const apiRef = useRef<GameApi>(null);

    const answerQuestion = useCallback(
        (answerIndex) => {
            const { id: questionId } = currentQuestion;
            const choice = (finalChoices || [])[answerIndex];
            const { id: choiceId = null } = choice || {};
            const { answerQuestion } = apiRef.current;
            answerQuestion(questionId, choiceId);
        },
        [currentQuestion, route, finalChoices, tracking],
    );

    useEffect(() => {
        api.set({});
        api.start({
            imageX: 0,
            imageScale: 1,
            imageRotate: 0,
            ctaScale: 1,
            growingScale: 0,
            leftAnswerOpacity: 0,
            rightAnswerOpacity: 0,
            allAnswersOpacity: 0,
            immediate: (key) => {
                switch (key) {
                    case 'imageX':
                    case 'imageScale':
                    case 'imageRotate':
                        return true;
                    default:
                        return false;
                }
            },
        });
    }, [currentQuestionIndex]);

    const bind = useDrag(
        ({ down, movement: [mx] }) => {
            const validPixels = width / 7.5;
            const leftAnswerSelected = mx <= -validPixels;
            const rightAnswerSelected = mx >= validPixels;
            const answerSelected = leftAnswerSelected || rightAnswerSelected;

            if (!down) {
                if (answerSelected) {
                    answerQuestion(leftAnswerSelected ? 0 : 1);
                }
            }

            let finalImageX = 0;
            let finalImageScale = 0.97;
            let finalImageRotate = mx / 20;
            let finalCtaScale = 0.85;
            let finalGrowingScale = Math.sign(mx) * 0.1 + (mx / validPixels) * 0.9;

            if (down) {
                finalImageX = mx;
            } else if (answerSelected) {
                finalImageX = leftAnswerSelected ? -width : width;
            } else {
                finalImageScale = 1;
                finalImageRotate = 0;
                finalCtaScale = 1;
                finalGrowingScale = 0;
            }

            api.start({
                imageX: finalImageX,
                imageScale: finalImageScale,
                imageRotate: finalImageRotate,
                ctaScale: finalCtaScale,
                growingScale: finalGrowingScale,
                leftAnswerOpacity: leftAnswerSelected ? 1 : 0,
                rightAnswerOpacity: rightAnswerSelected ? 1 : 0,
                allAnswersOpacity: leftAnswerSelected || rightAnswerSelected ? 1 : 0,
                immediate: (key) => {
                    switch (key) {
                        case 'imageX':
                        case 'imageRotate':
                        case 'growingScale':
                            return down;
                        default:
                            return false;
                    }
                },
            });
        },
        { enabled: !ended },
    );

    const onChoiceClick = useCallback(
        (choiceId) => {
            const index = finalChoices.findIndex(({ id }) => id === choiceId);
            const leftAnswerSelected = index === 0;
            const dir = leftAnswerSelected ? -1 : 1;

            api.start({
                imageX: leftAnswerSelected ? -width : width,
                imageScale: 1,
                imageRotate: 25 * dir,
                ctaScale: 0.85,
                growingScale: dir,
                leftAnswerOpacity: leftAnswerSelected ? 1 : 0,
                rightAnswerOpacity: !leftAnswerSelected ? 1 : 0,
                allAnswersOpacity: 1,
                immediate: (key) => key === 'growingScale',
            });
            answerQuestion(index);
        },
        [finalChoices, answerQuestion, width],
    );

    // Overwrite this
    const ThumbIconComponent = getComponentFromName(IconComponents, 'circle-valid');

    return (
        <BaseGame
            game={game}
            participation={participation}
            canPlay={canPlay}
            isValid={allAnswersValid}
            reload={reload}
            apiRef={apiRef}
            withFeedback
            endChildren={
                withQuestionsResults ? (
                    <QuestionsResults
                        withQuestionBody={questionsResultsWithBody}
                        itemComponent={questionsResultsItemComponent}
                        questions={questions}
                        answers={userAnswers}
                    />
                ) : null
            }
            className={classNames([styles.container, themeClassName, className])}
        >
            <div className={styles.gameContent}>
                <div className={styles.gameContentInner}>
                    <div className={styles.draggable} {...bind()}>
                        {/* <div className={styles.header}>
                            <div className={styles.titleContainer}>
                                {mediumImageUrl !== null ? (
                                    <img
                                        src={mediumImageUrl}
                                        alt={title}
                                        className={styles.headerImage}
                                    />
                                ) : (
                                    <Body className={styles.title} body={title} />
                                )}
                            </div>
                        </div> */}
                        <div className={styles.draggableInner}>
                            <div className={styles.questions}>
                                {(questions || []).map((question, questionIndex) => {
                                    const {
                                        body,
                                        image,
                                        title: questionTitle = null,
                                        category: questionCategory = null,
                                    } = question || {};
                                    const { url: imageUrl = null } = image || {};
                                    const isCurrentQuestion =
                                        questionIndex === currentQuestionIndex;
                                    return (
                                        <div
                                            className={classNames([
                                                styles.question,
                                                { [styles.current]: isCurrentQuestion },
                                            ])}
                                            key={`question-${questionIndex + 1}`}
                                            style={{ zIndex: questions.length - questionIndex }}
                                        >
                                            <div className={styles.questionHeader}>
                                                {questionCategory !== null ? (
                                                    <Body
                                                        className={styles.subtitle}
                                                        body={questionCategory}
                                                    />
                                                ) : null}
                                                {questionTitle !== null ? (
                                                    <Body
                                                        className={styles.title}
                                                        body={questionTitle}
                                                    />
                                                ) : null}
                                            </div>
                                            {isCurrentQuestion ? (
                                                <animated.div
                                                    className={styles.card}
                                                    style={
                                                        isCurrentQuestion
                                                            ? {
                                                                  x: imageX,
                                                                  rotate: imageRotate,
                                                                  scale: imageScale,
                                                              }
                                                            : null
                                                    }
                                                >
                                                    <div className={styles.cardInner}>
                                                        <div className={styles.arrows}>
                                                            <TinderArrowIcon
                                                                className={styles.arrow}
                                                                direction="left"
                                                            />
                                                            <TinderArrowIcon
                                                                className={styles.arrow}
                                                                direction="right"
                                                            />
                                                        </div>
                                                        <div
                                                            className={styles.image}
                                                            style={{
                                                                backgroundImage:
                                                                    imageUrl !== null
                                                                        ? `url(${imageUrl})`
                                                                        : null,
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={styles.labelContainer}>
                                                        <div className={styles.label}>{body}</div>
                                                    </div>
                                                </animated.div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                            {/* <div className={styles.results}>
                                 <div className={styles.score}>
                                    <div className={styles.count}>{`${currentQuestionIndex + 1}/${
                                        (questions || []).length
                                    }`}</div>
                                    <div className={styles.thumbs}>
                                        {(questions || []).map((question, questionIndex) => {
                                            const answered = userAnswers.length > questionIndex;
                                            const userAnswer = answered
                                                ? userAnswers[questionIndex]
                                                : null;
                                            const { valid: isRight = null } = userAnswer || {};
                                            const hasIconComponent = ThumbIconComponent !== null;
                                            return (
                                                <div
                                                    key={`thumb-${questionIndex + 1}`}
                                                    className={classNames([
                                                        styles.thumb,
                                                        {
                                                            [styles.answered]:
                                                                answered && isRight !== null,
                                                            [styles.isRight]: isRight,
                                                            [styles.isWrong]: !isRight,
                                                            [styles.withoutBox]: hasIconComponent,
                                                        },
                                                    ])}
                                                >
                                                    {hasIconComponent ? (
                                                        <ThumbIconComponent
                                                            current={
                                                                currentQuestionIndex ===
                                                                questionIndex
                                                            }
                                                            answered={answered}
                                                            valid={answered && isRight}
                                                        />
                                                    ) : (
                                                        <div className={styles.icon} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div> */}
                            <QuestionsIndicator
                                currentIndex={currentQuestionIndex}
                                questions={questions}
                                answers={userAnswers}
                                className={styles.results}
                                iconComponent={questionsIndicatorIconComponent}
                                currentIconComponent={questionsIndicatorCurrentIcon}
                                withProgress={questionsIndicatorWithProgress}
                            />
                            <div className={styles.instructions}>
                                {description !== null ? (
                                    <div>{description}</div>
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Answer the question by swiping left or right"
                                        description="Tinder game instructions"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.bottom}>
                        <div className={styles.bar}>
                            <animated.div
                                className={styles.growing}
                                style={{ scaleX: growingScale }}
                            >
                                <div className={styles.growingActive} />
                                <animated.div
                                    className={styles.growingSelected}
                                    style={{ opacity: allAnswersOpacity }}
                                />
                            </animated.div>
                            <div className={styles.choices}>
                                {(finalChoices || []).map(({ id, label }, answerIndex) => (
                                    <Button
                                        key={`answer-${id}-${answerIndex + 1}`}
                                        className={classNames([styles.choice, styles.choiceButton])}
                                        onClick={() => {
                                            onChoiceClick(id);
                                        }}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </div>
                            <div className={classNames([styles.choices, styles.choicesSelected])}>
                                {(finalChoices || []).map(({ id, label }, answerIndex) => (
                                    <animated.div
                                        key={`answer-${id}-${answerIndex + 1}`}
                                        className={styles.choice}
                                        style={{
                                            opacity:
                                                answerIndex === 0
                                                    ? leftAnswerOpacity
                                                    : rightAnswerOpacity,
                                        }}
                                    >
                                        {label}
                                    </animated.div>
                                ))}
                            </div>
                        </div>
                        <div className={styles.ctaContainer}>
                            <animated.div className={styles.cta} style={{ scale: ctaScale }}>
                                <img className={styles.hand} src={hand} alt="Hand" />
                            </animated.div>
                        </div>
                    </div>
                </div>
            </div>
        </BaseGame>
    );
}

export default TinderGame;
